
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CouponType } from "@/types/globalTypes";
import { couponeDiscount, formatMoney } from "@/utils/helpers";
import axios from "axios";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { AppConfig } from "@/utils/AppConfig";
import { clearCart } from "@/store/cart.slice";
import { phoneNumberRegex } from "@/utils/Regexes";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import CashierCartItem from "../Cashier/CashierCartItem";
import { clearTableCart } from "@/store/tableCartSlice";
import { CashierPostOrder } from "@/types/CashierProductType";
import TableCustomerInput from "./TableCustomerInput";
import TablePaymentModal from "./TablePaymentModal";
import { CartItem, invoice, TableData } from "@/types/ApiTypes";
import { useReactToPrint } from "react-to-print";
import { useSidebar } from "@/contexts/SidebarContext";


function TableCart({ taxActivated, tableId, floorId, setRefetch, total, data, tableData }: { taxActivated: boolean; tableId: string; floorId: string; setRefetch: React.Dispatch<React.SetStateAction<boolean>>; total: number; data: TableData; tableData: { floor: number; table: number } }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const cartItems = useSelector((state: RootState) => state.tableCart.cart);
    const dispatch = useDispatch();
    const {isShowingIconsOnly} = useSidebar()


    // Coupon states
    const [coupon, setCoupon] = useState<{ type: CouponType; amount: number }>({
        type: "percentage",
        amount: 0,
    });
    const [CouponError, setCouponError] = useState<string | null>(null);
    const [CopunSuccessMsg, setCouponSuccessMsg] = useState<string | null>(null);
    const [isCouponRequestPending, setIsCouponRequestPending] = useState(false);
    const [couponInputValue, setCouponInputValue] = useState<string>("");
    const [isPaymentModalOpened, setIsPaymentModalOpened] = useState(false);
    useLockBodyScroll(isPaymentModalOpened);




    //Subtotal price
    const subTotalPrice = total

    // Total Price
    const discountedPrice = couponeDiscount(
        subTotalPrice,
        coupon?.type,
        +coupon?.amount,
    );
    const lastPrintedOrderIdRef = useRef<invoice | null>(null);
    const totalPrice = discountedPrice > 0 ? discountedPrice : 0;
    const [order, setOrder] = useState<invoice | null>(null);
    // customerData
    const customerData = useSelector((state: RootState) => state.cart.customer);
    const contentRef = useRef<HTMLDivElement>(null);
    const toastIdRef = useRef<string | undefined>(undefined);
    const [printLoading, setPrintLoading] = useState(false)
    const selectedBranch = useSelector((state: RootState) => state.branch.branch);
    const isValidPhoneNumber =
        !!customerData.phone.match(phoneNumberRegex) || customerData.phone === "";

    const BASE_URL = import.meta.env.VITE_BASE_URL;
    const couponeDiscountInSars = subTotalPrice - totalPrice;
    const token = useSelector(
        (state: { auth: { token: string } }) => state.auth.token,
    );

    const FilteredCart = useMemo(
        () =>
            cartItems.map(({ _id, quantity, addons, options, notes }) => ({
                productId: _id,
                quantity,
                addons,
                options,
                notes,
            })),
        [cartItems],
    );

    const reactToPrintFn = useReactToPrint({
        contentRef,
        onBeforePrint: async () => {
            setPrintLoading(true);
            return Promise.resolve(); // or simply: return;
        },
        onAfterPrint: () => {
            setPrintLoading(false);
        },
        onPrintError: () => {
            setPrintLoading(false);
        },
    });
    useEffect(() => {
        if (printLoading) {
            // Show loading toast and store the ID
            toastIdRef.current = toast.loading(t("cashierHistoryModal.loading") as string);
        } else {
            if (toastIdRef.current) {
                // Dismiss loading toast
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = undefined;

                // Show success toast
                toast.success(t("cashierHistoryModal.printed") as string);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printLoading]);
    const handleSubmitOrder = async () => {

        const order: CashierPostOrder = {
            branch: selectedBranch?._id || "",
            items: FilteredCart,
            paymentData: {
                type: "pay-later",

            },
            coupon: CopunSuccessMsg ? couponInputValue : undefined,

            type: "internal",
            dineIn: {
                floorId: floorId,
                tableId: tableId,
            }

        };

        const makeOrderUrl = `${BASE_URL}/api/dashboard/cashier/make-order?locale=${lang}`;
        const headers = {
            Authorization: token,
            "Content-Type": "application/json",
        };

        try {
            const res = await axios.post(makeOrderUrl, order, { headers });

            if (res.data.success) {
                toast.success(
                    res.data.message ||
                    (t("cashierPaymentMethod.orderSuccess") as string),
                );
                // set order leads to auto print the order 

                dispatch(clearTableCart());

            } else {
                const message =
                    res.data.error || (t("cashierPaymentMethod.orderFailed") as string);
                toast.error(message);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const message =
                    error.response?.data?.error ||
                    (t("cashierPaymentMethod.orderFailed") as string);
                toast.error(message);
            } else {
                toast.error(t("cashierPaymentMethod.orderFailed") as string);
            }
        } finally {
            setRefetch((prev) => !prev);
        }
    };








    const handleCheckCoupon = async () => {
        if (!couponInputValue) return;

        const CheckCoupon = async (token: string, code: string, locale: string) => {
            const CheckCouponUrl = `${BASE_URL}/api/dashboard/cashier/apply-coupon?locale=${locale}`;
            const loadingToast = toast.loading(
                locale === "ar" ? "جاري التحقق من الكوبون" : "Checking Coupon ...",
            );

            const body = { code };

            const headers = {
                Authorization: token,
                "Content-Type": "application/json",
            };

            try {
                const { data } = await axios.post(CheckCouponUrl, body, { headers });

                if (!data.success) {
                    toast.error(
                        data.error ||
                        (t("cashierCards.failedDelete") as string),
                    );
                    return;
                }

                return data;
            } catch (error: unknown) {
                if (axios.isAxiosError(error)) {
                    const errorMessage =
                        error.response?.data?.error ||
                        (t("cashierCards.failedApply") as string);
                    toast.error(errorMessage);
                    return { success: false, error: errorMessage };
                }
                return { success: false, error: "Something went wrong" };
            } finally {
                toast.dismiss(loadingToast);
            }
        };

        setIsCouponRequestPending(true);

        const result = await CheckCoupon(token, couponInputValue, lang);

        if (result.success) {
            setCoupon(result?.data || { type: "percentage", amount: 0 });
            setCouponError(null);
            setCouponSuccessMsg(
                t("cashierCards.applied") as string,
            );
        } else {
            setCouponError(
                result?.error ||
                (t("cashierCards.failedApply") as string),
            );
            setCouponSuccessMsg(null);
        }

        setIsCouponRequestPending(false);
    };

    const clearCoupon = () => {
        setCouponSuccessMsg(null);
        setCouponError(null);
        setCoupon({ type: "percentage", amount: 0 });
        setCouponInputValue("");
    };

    const handleClearCartConfirmation = () => {
        const shouldClearCart = window.confirm(
            t("cashierCards.confirmClear") as string,
        );

        if (shouldClearCart) {
            dispatch(clearCart());
        }
    };

    const handleOpenPaymentModal = () => {
        if (isValidPhoneNumber) {
            setIsPaymentModalOpened(true);
        }
    };
    useEffect(() => {
        if (order) {
            if (lastPrintedOrderIdRef.current?._id === order._id) {
                return; // Same order, do nothing
            }

            lastPrintedOrderIdRef.current = order; // Update last printed ID
            reactToPrintFn();

            const timeout = setTimeout(() => {
                setOrder(null);

            }, 5000);

            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    return (
        <>
            {printLoading && (
                <div className="fixed inset-0 z-[9999] "></div>
            )}
            {order && <>

                <div dir="ltr" ref={contentRef} className="invoice-container text-lg font-semibold  print hidden">
                    <div className="text-center flex items-center flex-col">
                        <div className="w-44 h-auto">
                            <img src={order.logo} alt="Logo" className="w-full h-auto" />
                        </div>

                        <h1 className="text-xl font-bold flex flex-col">
                            <span>{order.restaurantData.nameAR === order.restaurantData.nameEN ? "" : order.restaurantData.nameAR}</span>
                            <span>{order.restaurantData.nameEN}</span>
                        </h1>
                        <h1 className="text-xl font-bold flex flex-col">
                            <span>{order.branch.nameAR === order.branch.nameEN ? "" : order.branch.nameAR}</span>
                            <span>{order.branch.nameEN}</span>
                        </h1>
                        <span className="font-semibold mt-1">{order.branch.address}</span>
                        <span className="font-semibold mt-1">{order.branch.phone}</span>

                        {order.taxNumber && <>
                            <div className="w-full flex  flex-col justify-between my-2 font-semibold">
                                <span>الرقم الضريبي </span>
                                <span>Tax number</span>
                            </div>
                            <div className="flex flex-col items-center gap-2 text-lg">
                                <span className="font-bold">{order.taxNumber}</span>
                            </div></>}

                        <div className="w-full flex flex-col justify-between my-2 font-semibold">
                            <span>فاتورة ضريبية مبسطة</span>
                            <span>Simple tax</span>
                        </div>
                        <span dir="ltr" className="flex border-2  gap-2 border-black p-2 font-bold w-fit text-2xl my-3">
                            Order# <div>{order.orderId}</div>
                        </span>

                        <span dir="ltr" className="font-semibold text-lg flex  gap-1">Unique order ID# <div>{order._id}</div></span>
                        <span className="font-semibold text-md flex gap-1 items-center ">Printed at : <div>{new Date().toLocaleString("en-US")}</div></span>
                        <div className="font-semibold text-md flex items-center gap-1">
                            <span>  Order time : </span>
                            <span >{order.deliverTime}</span>
                            <span> : وقت الطلب  </span>
                        </div>
                    </div>

                    <div className="w-full border-b-2 border-b-black" />


                    <div className="text-center font-semibold">
                        Cashier

                        <div className="flex justify-between text-sm">
                            <div>
                                Floor {tableData.floor || ""} - Table {tableData.table || ""}
                            </div>
                            <div dir="rtl">
                                طابق {tableData.floor || ""} - طاولة {tableData.table || ""}
                            </div>
                        </div>

                    </div>



                    <div className="w-full border-b-2 border-b-black" />

                    <div className=" space-y-1">
                        <div className="flex justify-between font-bold" dir="rtl">
                            <div className="text-start">Price</div>
                            <div className="flex gap-5">
                                <span>item</span>
                                <span>Qty</span>
                            </div>
                        </div>
                        <div className="my-2  w-full border-b-2 border-b-black" />
                        <div className=" font-semibold">
                            {order.items.map((item: CartItem, index) => {
                                return (
                                    <div key={index} className="flex flex-col gap-1">
                                        <div className="flex items-center gap-10  justify-between">
                                            <span className="whitespace-normal flex flex-row-reverse gap-5 " dir="rtl" ><span>{item.quantity} </span>
                                                <span>
                                                    <span className="flex flex-col gap-1">{item.nameEN}</span>
                                                    <span>{item.nameAR === item.nameEN ? "" : item.nameAR}</span>
                                                </span>
                                            </span>
                                            <span dir="ltr" className="flex items-center gap-1 self-start "> {AppConfig.currency.icon()}{formatMoney(item.totalPriceWithTaxes)}</span>
                                        </div>
                                        {item.addons.length > 0 && (
                                            <span className="flex flex-col font-bold">
                                                <span className="text-center">{t("orders.addons")}</span>
                                                <span className="font-normal flex flex-col gap-1 w-full">
                                                    {item.addons.map((addon, i) => {
                                                        const hasPrice = addon.price && addon.price > 0;
                                                        return hasPrice ? (
                                                            <div key={i} className="flex justify-between w-full text-sm">
                                                                <div></div>
                                                                <span className="text-right">{addon.nameEN}|{addon.nameAR}</span>
                                                                <span dir="ltr">
                                                                    {AppConfig.currency.icon()} {formatMoney(addon.price)}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div key={i} className="flex justify-center w-full text-sm">
                                                                <span className="text-right">{addon.nameEN} | {addon.nameAR}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </span>
                                            </span>
                                        )}


                                        {item.options.length > 0 && (
                                            <span className="flex flex-col font-bold">
                                                <span className="text-center">{t("ordersBody.Options")}</span>
                                                <span className="font-normal flex flex-col gap-1 w-full">
                                                    {item.options.map((option, i) =>
                                                        option.options.map((opt, j) => {
                                                            const hasPrice = opt.price && opt.price > 0;
                                                            return hasPrice ? (
                                                                <div key={`${i}-${j}`} className="flex justify-between w-full text-sm">
                                                                    <div></div> {/* Optional for alignment */}
                                                                    <span className="text-right">{opt.nameEN}|{opt.nameAR}</span>
                                                                    {opt.price && opt.price > 0 && (<span dir="ltr">
                                                                        {AppConfig.currency.icon()} {formatMoney(opt.price)}
                                                                    </span>)}
                                                                </div>
                                                            ) : (
                                                                <div key={`${i}-${j}`} className="flex justify-center w-full text-sm">
                                                                    <span className="text-right">{opt.nameEN} | {opt.nameAR}</span>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </span>
                                            </span>
                                        )}
                                        {order.items.length - 1 !== index && <div className="w-full border-b-2 border-b-black" />}
                                    </div>


                                )
                            })}
                        </div>


                    </div>


                    {order.subtotal > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Sub total</div>
                                    <div>المجموع الفرعي</div>
                                </div>
                                <div className="text-center">
                                    {AppConfig.currency.icon()} {formatMoney(order.subtotal)}
                                </div>
                            </div>
                        </>
                    )}

                    {order.discount > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Discount</div>
                                <div>الخصم</div>
                            </div>
                            <div className="text-center">
                                {AppConfig.currency.icon()} {formatMoney(order.discount)}
                            </div>
                        </div>
                    )}



                    {order.taxes > 0 && (
                        <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                            <div className="flex flex-col text-end w-[50%]">
                                <div>Taxes (15%)</div>
                                <div>الضرائب (15%)</div>
                            </div>
                            <div className="text-center">
                                {AppConfig.currency.icon()} {formatMoney(order.taxes)}
                            </div>
                        </div>
                    )}
                    <div
                        dir="ltr"
                        className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                    >
                        <div className="flex w-[50%] flex-col text-end">
                            <div>Service Fees</div>
                            <div>رسوم الخدمة</div>
                        </div>
                        <div className="text-center">
                            {AppConfig.currency.icon()} {formatMoney(order.serviceFees || 0)}
                        </div>
                    </div>
                    {order.total > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Total</div>
                                    <div>المجموع</div>
                                </div>
                                <div className="text-center">
                                    {AppConfig.currency.icon()} {formatMoney(order.total)}
                                </div>
                            </div>
                        </>
                    )}

                    {order.cashPaid > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Total Cash Paid</div>
                                    <div>إجمالي النقد المدفوع</div>
                                </div>
                                <div className="text-center">
                                    {AppConfig.currency.icon()} {formatMoney(order.cashPaid)}
                                </div>
                            </div>
                        </>
                    )}

                    {order.networkPaid > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Total Credit Paid</div>
                                    <div>إجمالي الشبكة المدفوعة</div>
                                </div>
                                <div className="text-center">
                                    {AppConfig.currency.icon()} {formatMoney(order.networkPaid)}
                                </div>
                            </div>
                        </>
                    )}

                    {order.remainingCash > 0 && (
                        <>
                            <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                            <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                                <div className="flex flex-col text-end w-[50%]">
                                    <div>Total Remaining Cash</div>
                                    <div>إجمالي النقد المتبقي</div>
                                </div>
                                <div className="text-center">
                                    {AppConfig.currency.icon()} {formatMoney(order.remainingCash)}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                    <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between mt-2">
                        <div className="flex flex-col text-end w-[50%]">
                            <div>No. Products</div>
                            <div>عدد المنتجات</div>
                        </div>
                        <div className="text-center">{order.totalItems}</div>
                    </div>

                    <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                    <div dir="ltr" className="flex  font-bold text-sm items-center justify-between w-full ml-auto ">
                        <div className=" text-center">
                            {order.paymentMethod}
                        </div>
                        <div className="flex flex-col  text-end ">
                            <div>Payment method</div>
                            <div>طريقة الدفع</div>
                        </div>
                    </div>

                    <div className="text-center flex items-center justify-center flex-col">
                        <div className="w-52 h-52 ">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg"
                                alt="QR"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>
                </div>


            </>}
            <div className={`col-span-4 flex h-[600px] flex-col gap-4 rounded-md border border-gray-300 bg-lightSkyBlue ${isShowingIconsOnly ? "lg:col-span-7" : "lg:col-span-6"} ${isShowingIconsOnly ? "break2lg-col-span-6" : "break2lg-col-span-7"}  2xl:col-span-4`}>

                <>
                    {/*  customer data */}
                    <TableCustomerInput isValidPhoneNumber={isValidPhoneNumber} customer={data.customer} />

                    {/* items listing */}
                    <div className="thin-scrollbar h-full overflow-y-auto border-b border-gray-300">
                        {cartItems.map((item, i) => (
                            <CashierCartItem key={i} item={item} isTable={true} />
                        ))}
                    </div>

                    {/* coupon input */}
                    <div className="mt-auto flex flex-col gap-1 px-2">
                        <div className="flex gap-2 rounded-md">
                            <div className="relative size-full">
                                <input
                                    type="text"
                                    name="coupon"
                                    id="coupon"
                                    className="w-full rounded-md border border-gray-300 p-[9px] pe-8"
                                    placeholder={
                                        t("cashierCards.enterCode") as string
                                    }
                                    onChange={(e) => {
                                        setCouponInputValue(e.target.value);
                                        setCoupon({
                                            type: "percentage",
                                            amount: 0,
                                        });
                                        setCouponSuccessMsg(null);
                                        setCouponError(null);
                                    }}
                                    value={couponInputValue}
                                    disabled={isCouponRequestPending}
                                />
                                {couponInputValue && (
                                    <button
                                        type="button"
                                        className="absolute end-2 top-1/2 -translate-y-1/2"
                                        onClick={clearCoupon}
                                        disabled={isCouponRequestPending}
                                    >
                                        <X className="h-5 w-5 text-red-500" />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleCheckCoupon}
                                disabled={isCouponRequestPending}
                                className="rounded-md bg-sidebar px-10 text-lg text-skyblue disabled:cursor-not-allowed"
                            >
                                {t("cashierCards.apply")}
                            </button>
                        </div>
                        {CouponError ? (
                            <div className="text-sm text-red-500">{CouponError}</div>
                        ) : null}
                        {CopunSuccessMsg ? (
                            <div className="text-sm text-green-500">
                                {CopunSuccessMsg}{" "}
                                <span dir="rtl">
                                    ({" "}
                                    {couponeDiscountInSars.toLocaleString("en-EG", {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    })}{" "}
                                    <span className="me-1 inline-block">
                                        {AppConfig.currency.icon()} )
                                    </span>
                                </span>
                            </div>
                        ) : null}
                    </div>

                    {/* total & tax */}
                    <div className="flex flex-col items-center justify-center gap-y-3 px-2">
                        {taxActivated && (
                            <p className="flex items-center gap-2 rounded-lg bg-gray-300 px-2 py-1 text-lg font-[500] text-gray-500">
                                <span>  {t("cashierCards.tax")}</span>
                                <span className="flex items-center gap-1" dir="rtl">
                                    {" "}
                                    {(totalPrice - (totalPrice / 1.15)).toLocaleString("en-EG", {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    })}
                                    {AppConfig.currency.icon()}
                                </span>
                            </p>
                        )}

                        <div className="mb-3 flex w-full items-center justify-center gap-x-2 text-base 2xl:text-lg">
                            <div className="space-y-2">
                                <button
                                    onClick={handleSubmitOrder}
                                    className="w-full min-w-fit rounded-lg bg-sidebar px-3 py-2  font-bold text-white"
                                >
                                    <p>
                                        {t("cashierCards.addToCart")}
                                    </p>
                                </button>
                                <button
                                    onClick={handleOpenPaymentModal}
                                    className="w-full min-w-fit rounded-lg bg-sidebar px-3 py-2  font-bold text-white"
                                >
                                    <p>
                                        <span>{t("cashierCards.totalPayment")} </span>

                                        <span dir="rtl">
                                            {formatMoney(total)}
                                            {AppConfig.currency.icon()}
                                        </span>
                                    </p>
                                </button>
                            </div>

                            <button
                                className="min-w-fit rounded-lg bg-red-400 px-3 py-2  font-bold text-white duration-100 hover:bg-red-500"
                                onClick={handleClearCartConfirmation}
                            >
                                {t("cashierCards.clear")}
                            </button>
                        </div>
                    </div>
                </>

                {isPaymentModalOpened && (
                    <TablePaymentModal
                        setOrder={setOrder}
                        setRefetch={setRefetch}
                        isValidPhoneNumber={isValidPhoneNumber}
                        setIsPaymentModalOpened={setIsPaymentModalOpened}
                        FilteredCart={FilteredCart}
                        couponInputValue={couponInputValue}
                        CopunSuccessMsg={CopunSuccessMsg}
                        totalPrice={totalPrice}
                        data={data}
                    />
                )}
            </div>
        </>
    );
}

export default TableCart;
